import { message, Modal } from 'antd';
import { OrgDataContext } from 'context/orgData';
import { useContext, useEffect, useMemo, useReducer } from 'react';
import { selectQuartermasterViewData } from '../selectors/quartermaster-view-data';
import {
	INITIAL_QUARTERMASTER_STATE,
	PobSection,
	QMCurrentState,
	quartermasterReducer,
} from '../state';
import {useQuartermasterQuery} from './use-quartermaster-query';
import { usePobActions } from './use-pob-actions';
import { useQuartermasterData } from './use-quartermaster-data';

const POB_SEARCH_DEBOUNCE_MS = 1000;

export function useQuartermaster() {
	const orgData = useContext(OrgDataContext);
	const [state, dispatch] = useReducer(quartermasterReducer, INITIAL_QUARTERMASTER_STATE);
	const {
		rigData,
		pobNodeData,
		sidebarLoading,
		pageLoading,
		refetchPobNodes,
	} = useQuartermasterData({
		customerID: orgData.customerID,
		rigID: state.sidebarState.rigId,
	});
	const {
		loadExpectedPassengers,
		departPassengers,
		handleOnDragEnd,
		addPob,
		loadingExpectedPassengers,
		loadingDepartingPassengers,
		submittingNewPobNode,
	} = usePobActions({
		customerID: orgData.customerID,
		transporterID: orgData.transporterID,
		rigID: state.sidebarState.rigId,
		pobNodeData,
		refetchPobNodes,
	});

	useEffect(() => {
		if (state.pobSearchInput === state.pobSearchValue) return;

		const timeout = setTimeout(
			() =>
				dispatch({
					type: 'apply-pob-search',
					value: state.pobSearchInput,
				}),
			POB_SEARCH_DEBOUNCE_MS
		);

		return () => clearTimeout(timeout);
	}, [state.pobSearchInput, state.pobSearchValue]);

	const viewData = useMemo(
		() =>
			selectQuartermasterViewData(
				pobNodeData,
				state.sidebarState.rigId,
				state.sidebarState.carrierId,
				state.searchValue,
				state.pobSearchValue
			),
		[
			pobNodeData,
			state.searchValue,
			state.pobSearchValue,
			state.sidebarState.carrierId,
			state.sidebarState.rigId,
		]
	);

    useQuartermasterQuery(rigData, viewData.inTransit, state.sidebarState.rigId,
        state.sidebarState.carrierId, dispatch);

	const expectedSelectionEnabled = Boolean(
		state.sidebarState.rigId && state.sidebarState.carrierId
	);
	const departingSelectionEnabled = Boolean(state.sidebarState.rigId);
	const visibleSelectedPobIds = useMemo(() => {
		const result = {
			onboard: [],
			expected: [],
			departing: [],
		} as Record<PobSection, string[]>;

		(Object.keys(result) as PobSection[]).forEach((section) => {
			const visibleIDs = new Set(
				viewData.passengerLists[section].map((pob) => pob._id)
			);
			result[section] = state.selectedPobIds[section].filter((id) => visibleIDs.has(id));
		});
		return result;
	}, [viewData.passengerLists, state.selectedPobIds]);

	function setAddPobDrawerOpen(open: boolean) {
		dispatch({ type: 'set-add-pob-open', open });
	}

	function setEditPobDrawerOpen(open: boolean, pobID?: string) {
		dispatch({ type: 'set-edit-pob-open', open, pobID });
	}

	function setPobSearchInput(value: string) {
		dispatch({ type: 'set-pob-search-input', value });
	}

	function togglePobSelection(section: PobSection, pobID: string) {
		dispatch({ type: 'toggle-pob-selection', section, pobID });
	}

	function setAllPobSelected(section: PobSection, selected: boolean) {
		dispatch({
			type: 'set-pob-selection',
			section,
			pobIDs: selected
				? viewData.passengerLists[section].map((pob) => pob._id)
				: [],
		});
	}

	function getPassengerIDsForTransition(section: PobSection) {
		const selectedIDs = visibleSelectedPobIds[section];
		return selectedIDs.length > 0
			? selectedIDs
			: viewData.passengerLists[section].map((pob) => pob._id);
	}

	function confirmLoadPassengers(section: PobSection) {
		if (section !== 'expected' || !expectedSelectionEnabled) return;

		const pobNodeIds = getPassengerIDsForTransition(section);
		if (pobNodeIds.length === 0) return;

		const confirmationMessage =
			`Are you sure? This will pull ${pobNodeIds.length} passengers off flight ` +
			`${state.sidebarState.carrierName} and put them onboard location ` +
			`${state.sidebarState.locationName}. This operation cannot be undone!`;

		Modal.confirm({
			title: 'Load passengers',
			content: confirmationMessage,
			okText: 'Load',
			onOk: () =>
				loadExpectedPassengers({
					pobNodeIds,
					flightID: state.sidebarState.carrierId,
				})
					.then(() => dispatch({ type: 'clear-pob-selection', section }))
					.catch(() => message.error('Failed to load passengers.')),
		});
	}

	function confirmDepartPassengers(section: PobSection) {
		if (section !== 'departing' || !departingSelectionEnabled) return;

		const pobNodeIds = getPassengerIDsForTransition(section);
		if (pobNodeIds.length === 0) return;

		const flightDescription = state.sidebarState.carrierId
			? ` on flight ${state.sidebarState.carrierName}`
			: '';
		const confirmationMessage =
			`Are you sure? This will depart ${pobNodeIds.length} passengers from location ` +
			`${state.sidebarState.locationName}${flightDescription}. ` +
			'This operation cannot be undone!';

		Modal.confirm({
			title: 'Depart passengers',
			content: confirmationMessage,
			okText: 'Depart',
			onOk: () =>
				departPassengers({ pobNodeIds })
					.then(() => dispatch({ type: 'clear-pob-selection', section }))
					.catch(() => message.error('Failed to depart passengers.')),
		});
	}

	function handleAddPobSubmit(pobData: any) {
		addPob(pobData)
			.then(() => {
				message.success('Created passenger successfully');
				setAddPobDrawerOpen(false);
			})
			.catch(() => message.error('Failed to create passenger'));
	}

	const contextValue: QMCurrentState = {
		...state,
		setAddPobDrawerOpen,
		setEditPobDrawerOpen,
	};

	return {
		state,
		dispatch,
		contextValue,
		rigData,
		passengerLists: viewData.passengerLists,
		inTransit: viewData.inTransit,
		expectedSelectionEnabled,
		departingSelectionEnabled,
		selectedPobIds: visibleSelectedPobIds,
		loadingExpectedPassengers,
		loadingDepartingPassengers,
		submittingNewPobNode,
		sidebarLoading,
		pageLoading,
		setAddPobDrawerOpen,
		setEditPobDrawerOpen,
		setPobSearchInput,
		togglePobSelection,
		setAllPobSelected,
		confirmLoadPassengers,
		confirmDepartPassengers,
		handleOnDragEnd,
		handleAddPobSubmit,
	};
}
